import {
  HStack,
  VStack,
  Image,
  Button,
  Text,
  Box,
  Spinner,
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import { useCallback, useState, useRef } from "react";
import { useLoading } from "src/Manager/hooks/form";
import ReactS3Uploader, { S3Response } from "react-s3-uploader";
import KYDInput from "./KYDInput";

const KYDImageUpload = ({
  image,
  link,
  supportLink = false,
  onChange,
  isDisabled = false,
  createUploadUrl,
}: {
  image: string | null;
  link?: string;
  supportLink?: boolean;
  onChange: (result: { cdn_url: string; link?: string }) => void;
  createUploadUrl: ({
    file,
    callback,
  }: {
    file: File;
    callback: ({ signedUrl }) => void | Promise<void | string>;
  }) => Promise<{ signedUrl: string; fileKey: string }>;
  isDisabled?: boolean;
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useLoading({
    upload: false,
  });
  const [progress, setProgress] = useState(0);
  const dragCounter = useRef(0);
  const uploadRef = useRef<HTMLInputElement>(null);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (dragCounter.current === 1) {
      setIsDragging(true);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setIsDragging(false);

      const file = e.dataTransfer.files[0];
      // FIND A WAY TO UPLOAD THIS FILE
      console.log("Dropped a file", file);
    },
    [onChange]
  );

  return (
    <VStack
      w="100%"
      borderRadius="8px"
      p={3}
      bg="#F5F5F5"
      gap={3}
      alignItems="center"
      justifyContent="center"
      transition="border 0.2s ease"
    >
      <HStack
        borderRadius="8px"
        gap={3}
        w="100%"
        alignItems="center"
        justifyContent="center"
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        border={isDragging ? "2px dashed #3182CE" : "0px dashed transparent"}
        transition="border 0.2s ease"
      >
        <ReactS3Uploader
          style={{ display: "none" }}
          //@ts-ignore
          inputRef={uploadRef}
          getSignedUrl={(file, callback) => createUploadUrl({ file, callback })}
          accept="image/*"
          onProgress={(p) => {
            setProgress(p);
          }}
          preprocess={(f, next) => {
            setLoading("upload", true);
            next(f);
          }}
          onError={(e) => {
            console.log("onError", e);
            console.error(e);
          }}
          onFinish={(result: S3Response & { cdn_url: string }) => {
            setLoading("upload", false);
            onChange({
              cdn_url: result.cdn_url,
              link: link,
            });
          }}
          uploadRequestHeaders={(file) => ({
            "Content-Type": file.type,
            "x-amz-acl": "private",
          })}
          contentDisposition="auto"
        />
        {loading.upload ? (
          <Box
            draggable={false}
            onDragStart={(e) => e.preventDefault()}
            width="150px"
            height="150px"
            borderRadius="2px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Spinner />
          </Box>
        ) : (
          image && (
            <Box
              draggable={false}
              onDragStart={(e) => e.preventDefault()}
              width="150px"
              borderRadius="2px"
            >
              <Image
                boxSize="100px"
                src={image}
                bg="transparent"
                boxShadow="0px 4px 10px rgba(0,0,0,0.1), 0px 2px 4px rgba(0,0,0,0.05)"
                objectFit="cover"
                alt="preview"
                borderRadius="2px"
              />
            </Box>
          )
        )}
        <VStack w="100%">
          <Button
            type="button"
            onClick={() => uploadRef.current?.click()}
            isDisabled={isDisabled}
            border="1px solid #0000001A"
            variant="outline"
            fontWeight="400"
            colorScheme="gray"
            fontSize="13px"
            gap={2}
            borderRadius="50px"
          >
            <AddIcon color="#5D5D5D" />
            Add Image
          </Button>
          <Text fontSize="12px" color="#1A202CB2">
            Drag & drop file here
          </Text>
        </VStack>
      </HStack>
      {supportLink && (
        <KYDInput
          label="Link"
          placeholder="Add link to redirect when image is clicked"
          value={link}
          onChange={(e) => {
            onChange({
              cdn_url: image,
              link: e.target.value,
            });
          }}
        />
      )}
    </VStack>
  );
};

export default KYDImageUpload;
